import {
  Box,
  ButtonBase,
  styled,
  Typography,
  useTheme
} from "@material-ui/core";
import clsx from "clsx";
import { graphql } from "gatsby";
import React from "react";
import { useToggle } from "react-use";
import { formatMultilinesText } from "../../helpers/markdown";
import PlusIcon from "./PlusIcon";

const Wrapper = styled("div")(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(4),
  borderRadius: theme.spacing(1),
  flex: 1,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.05)",
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4, 2.5)
  },
  "& .front": {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    backgroundColor: theme.palette.primary.main,
    padding: theme.spacing(4),
    opacity: 0,
    transition: "opacity 250ms ease-in-out",
    overflowY: "auto",
    "&.active": {
      opacity: 1,
      transition: "opacity 250ms ease-in-out"
    }
  }
}));

const NumberTypography = styled(Typography)(({ theme }) => ({
  fontSize: "5rem",
  lineHeight: "5.6rem",
  color: theme.palette.primary.main,
  paddingBottom: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  [theme.breakpoints.down("sm")]: {
    fontSize: "4rem",
    lineHeight: "4.2rem"
  }
}));

const TitleTypography = styled(Typography)(({ theme }) => ({
  fontSize: "4rem",
  lineHeight: "4.2rem",
  [theme.breakpoints.down("md")]: {
    fontSize: "2.4rem",
    lineHeight: "3rem"
  }
}));

const TextTypography = styled(Typography)(({ theme }) => ({
  fontSize: "1.6rem",
  lineHeight: "2.4rem",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "2.2rem",
    marginBottom: theme.spacing(4)
  }
}));

const CircleButton = styled(ButtonBase)(({ theme }) => ({
  width: "32px",
  height: "32px",
  background: theme.palette.grey[300],
  borderRadius: "50%",
  transform: "rotate(0deg)",
  transition: "transform 250ms ease-in-out, background 250ms ease-in-out",
  zIndex: 1,
  "&.active": {
    transform: "rotate(45deg)",
    transition: "transform 250ms ease-in-out, background 250ms ease-in-out",
    background: theme.palette.common.white
  },
  [theme.breakpoints.down("sm")]: {
    marginBottom: "-8px"
  }
}));

const Card = ({
  number,
  title,
  text
}: {
  number: number;
  title: string;
  text: string;
}) => {
  const [active, toggleActive] = useToggle(false);

  return (
    <Wrapper>
      <Box mb={{ xs: 6, md: 10 }}>
        <NumberTypography variant="h4">{number}</NumberTypography>
        <TitleTypography variant="h4" style={{ lineHeight: 1.05 }}>
          {title}
        </TitleTypography>
      </Box>

      <CircleButton className={clsx({ active: active })} onClick={toggleActive}>
        <PlusIcon />
      </CircleButton>

      <div className={clsx({ front: true, active })}>
        <TextTypography
          dangerouslySetInnerHTML={{ __html: formatMultilinesText(text) }}
        />
      </div>
    </Wrapper>
  );
};

export default function PartnersFlipcard({
  card1Title,
  card1Text,
  card2Title,
  card2Text,
  card3Title,
  card3Text
}: Queries.PartnersFlipcardFragmentFragment) {
  const theme = useTheme();
  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", md: "row" }}
      style={{ gap: theme.spacing(3) }}
    >
      <Card number={1} title={card1Title} text={card1Text} />
      <Card number={2} title={card2Title} text={card2Text} />
      <Card number={3} title={card3Title} text={card3Text} />
    </Box>
  );
}

export const PartnersFlipcardFragment = graphql`
  fragment PartnersFlipcardFragment on DatoCmsCModulePartnersFlipcard {
    card1Title
    card1Text
    card2Title
    card2Text
    card3Title
    card3Text
    container
    containerDisableGutters
    containerBackgroundColor
    model {
      apiKey
    }
  }
`;
